// MCHD Room Scheduler - Floor Management API Endpoints
// API calls for floor management operations
// Created: 09-10-2025 13:26:21

import { TokenManager } from '../interceptors';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:7991/api/v1';

// =============================================================================
// TYPES
// =============================================================================

export interface Floor {
  id: string;
  number: number;
  name: string;
  buildingId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FloorWithDetails extends Floor {
  building: {
    id: string;
    name: string;
  };
  roomCount: number;
}

export interface CreateFloorData {
  buildingId: string;
  number: number;
  name: string;
}

export interface BulkCreateFloorsData {
  buildingId: string;
  floors: Array<{
    number: number;
    name: string;
  }>;
}

export interface CheckFloorAvailabilityResponse {
  success: boolean;
  data?: {
    available: boolean;
    existingFloor?: FloorWithDetails;
  };
  error?: {
    code: string;
    message: string;
  };
}

export interface CreateFloorResponse {
  success: boolean;
  data?: FloorWithDetails;
  error?: {
    code: string;
    message: string;
  };
}

export interface BulkCreateFloorsResponse {
  success: boolean;
  data?: FloorWithDetails[];
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  meta?: {
    count: number;
    buildingId: string;
    buildingName: string;
  };
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Create a single floor
 * POST /api/v1/floors
 * Created: 09-10-2025 13:26:21
 * @param data - Floor creation data
 * @returns Promise<CreateFloorResponse>
 */
export async function createFloor(data: CreateFloorData): Promise<CreateFloorResponse> {
  try {
    const token = TokenManager.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/floors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || {
          code: 'UNKNOWN_ERROR',
          message: 'Failed to create floor',
        },
      };
    }

    return {
      success: true,
      data: result.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error.message || 'Network error occurred',
      },
    };
  }
}

/**
 * Bulk create floors
 * POST /api/v1/floors/bulk
 * Created: 09-10-2025 13:26:21
 * @param data - Bulk floor creation data
 * @returns Promise<BulkCreateFloorsResponse>
 */
export async function bulkCreateFloors(data: BulkCreateFloorsData): Promise<BulkCreateFloorsResponse> {
  try {
    const token = TokenManager.getAccessToken();
    const response = await fetch(`${API_BASE_URL}/floors/bulk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || {
          code: 'UNKNOWN_ERROR',
          message: 'Failed to bulk create floors',
        },
      };
    }

    return {
      success: true,
      data: result.data,
      meta: result.meta,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error.message || 'Network error occurred',
      },
    };
  }
}

/**
 * Check floor availability
 * GET /api/v1/floors/check-availability?buildingId=X&number=Y
 * Created: 09-10-2025 13:26:21
 * @param buildingId - Building ID
 * @param floorNumber - Floor number to check
 * @returns Promise<CheckFloorAvailabilityResponse>
 */
export async function checkFloorAvailability(
  buildingId: string,
  floorNumber: number
): Promise<CheckFloorAvailabilityResponse> {
  try {
    const token = TokenManager.getAccessToken();
    const url = new URL(`${API_BASE_URL}/floors/check-availability`);
    url.searchParams.append('buildingId', buildingId);
    url.searchParams.append('number', floorNumber.toString());

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || {
          code: 'UNKNOWN_ERROR',
          message: 'Failed to check floor availability',
        },
      };
    }

    return {
      success: true,
      data: result.data,
    };
  } catch (error: any) {
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error.message || 'Network error occurred',
      },
    };
  }
}

